import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Sparkles, Bot, RotateCcw, User, ArrowUpRight } from 'lucide-react';

const AIAssistantKnowledge = [
  {
    keywords: ['skill', 'tech', 'stack', 'languages', 'framework', 'database', 'tools', 'know', 'expertise', 'capabilities'],
    title: "🛠️ Tech Stack & Skills",
    response: `Karthik is a highly skilled **Full-Stack Developer (MERN Stack)** with special focus on system scalability and cloud architecture. Here is his technical inventory:

*   **Frontend**: React, Next.js, TypeScript, Tailwind CSS, Framer Motion
*   **Backend**: Node.js, Express, Microservices, RESTful APIs, WebSockets
*   **Databases & Caching**: PostgreSQL, MongoDB, Redis
*   **Cloud & DevOps**: Microsoft Azure (AZ-900 Certified), Oracle Cloud, Docker, Kubernetes
*   **Core Systems**: High-Level & Low-Level System Design (HLD/LLD), Secure Auth/OAuth, Aggregation Pipelines`
  },
  {
    keywords: ['project', 'portfolio', 'implementations', 'build', 'codebase', 'applications', 'system'],
    title: "📂 Key Projects",
    response: `Karthik has built several high-performance applications focused on scale and complexity:

1.  **AI Mentor Chatbot** (AI & ML)
    *   *MERN Stack, Gemini 2.5 Flash, LangChain, WebSockets, Azure*
    *   Intelligent mentorship platform with live chat & personalized career skill mapping.
2.  **Service-Mate** (Full Stack)
    *   *Node.js, Redis, Socket.io, MongoDB*
    *   Real-time service booking system with instant notifications, provider tracking, and optimized scheduler query pipelines.
3.  **Emotify - AI Powered Music Player** (AI & ML)
    *   *React, Face-api.js, Spotify API, Express*
    *   Mood-synchronized playlist curation using live in-browser facial emotion recognition.
4.  **TravelDeskX** (Systems & Performance)
    *   *React, Tailwind, REST APIs, Vite*
    *   Enterprise travel portal featuring zero-latency instant navigation and multi-city route engines.`
  },
  {
    keywords: ['experience', 'work', 'job', 'intern', 'journey', 'genesis', 'tekavach', 'role', 'career'],
    title: "💼 Professional Journey",
    response: `Karthik is currently targetting Full-Time **Software Development Engineer (SDE)** roles and internships. His experience includes:

*   **Genesis** — *SDE Intern (Feb 2026 - May 2026)*
    Designed scalable RESTful APIs, architected React frontends, optimized MongoDB data layers, and deployed services on Azure using Agile workflows.
*   **Tekavach** — *Software Development Engineer (Internship) (Feb 2026 - May 2026)*
    Engineered AI platform logic and assisted in full-stack feature releases.
*   **IEEE National Conference** — *Technical Lead & Paper Reviewer (Nov 2025)*
*   **Innovatiaa Hackathon 1.0** — *Technical Lead (Sept 2025)*`
  },
  {
    keywords: ['education', 'college', 'degree', 'university', 'study', 'grade', 'cgpa', 'academics', 'smce'],
    title: "🎓 Academic Foundation",
    response: `Karthik is a 3rd-year **Computer Science Engineering** student with a solid theoretical and practical background:

*   **Institution**: Dr Sri Sri Sri Shivakumara Mahaswamy College of Engineering
*   **Degree**: Bachelor of Engineering (BE) in Computer Science
*   **Timeline**: Nov 2022 - Nov 2026
*   **CGPA**: **8.5 / 10**
*   **Core Focus**: Data Structures & Algorithms (DSA), System Design, Database Management Systems (DBMS), Operating Systems (OS), and Computer Networks.`
  },
  {
    keywords: ['contact', 'email', 'mail', 'phone', 'reach', 'hire', 'linkedin', 'github', 'social', 'address'],
    title: "✉️ Contact Details",
    response: `You can reach out to Karthik directly through the following channels:

*   **Email**: [kartikagouda644@gmail.com](mailto:kartikagouda644@gmail.com)
*   **WhatsApp**: [+91 8296670391](https://wa.me/918296670391)
*   **LinkedIn**: [linkedin.com/in/karthikgouda15](https://www.linkedin.com/in/karthikgouda15/)
*   **GitHub**: [github.com/Karthikgouda15](https://github.com/Karthikgouda15)

*Feel free to use the Contact section at the bottom of the landing page to send him a real-time encrypted message!*`
  }
];

const FallbackReplies = [
  "I understand you're asking about that! Karthik's AI Core is optimized to discuss his engineering capabilities, projects, education, and career history. Could you rephrase or try one of the quick options below?",
  "That sounds fascinating! As Karthik's AI Representative, I'm best equipped to tell you about his MERN stack skills, Azure deployments, projects like the AI Mentor Chatbot, or his SDE internship at Genesis. Let me know what you'd like to explore!",
  "Great question! To get specific details on that or discuss potential collaborations, you can shoot Karthik a quick email at [kartikagouda644@gmail.com](mailto:kartikagouda644@gmail.com), or check out his professional network links below!"
];

const AIAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      sender: 'bot',
      text: "Hello! I am Gouda AI, Karthik's AI Assistant. 🤖✨\n\nI can provide real-time details about his software development skills, full-stack projects, SDE internship at Genesis, or help you connect with him. What would you like to explore?",
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [inputVal, setInputVal] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showPills, setShowPills] = useState(true);

  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isTyping, isOpen]);

  // Markdown parser to convert raw [label](url) text links to live clickable <a> tags
  const renderMessageText = (text) => {
    const parts = [];
    const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      const matchIndex = match.index;
      // Add preceding plain text
      if (matchIndex > lastIndex) {
        parts.push(text.substring(lastIndex, matchIndex));
      }
      
      const label = match[1];
      const url = match[2];
      
      parts.push(
        <a 
          key={matchIndex} 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-secondary hover:text-secondary/80 underline font-black inline-flex items-center gap-0.5"
        >
          {label}
          <ArrowUpRight size={10} className="inline-block" />
        </a>
      );
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  const handleQuery = (queryText) => {
    if (!queryText.trim()) return;

    // Add user message
    const userMsg = {
      id: Date.now().toString(),
      sender: 'user',
      text: queryText,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages(prev => [...prev, userMsg]);
    setInputVal('');
    setIsTyping(true);
    setShowPills(false);

    // AI logic response matching
    setTimeout(() => {
      const normalizedQuery = queryText.toLowerCase();
      let matchedResponse = null;

      // Find keyword match
      for (const item of AIAssistantKnowledge) {
        if (item.keywords.some(keyword => normalizedQuery.includes(keyword))) {
          matchedResponse = item.response;
          break;
        }
      }

      // Handle fallback
      if (!matchedResponse) {
        matchedResponse = FallbackReplies[Math.floor(Math.random() * FallbackReplies.length)];
      }

      const botMsg = {
        id: (Date.now() + 1).toString(),
        sender: 'bot',
        text: matchedResponse,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      setMessages(prev => [...prev, botMsg]);
      setIsTyping(false);
    }, Math.random() * 400 + 400); // realistic typing wait time
  };

  const resetChat = () => {
    setMessages([
      {
        id: 'welcome',
        sender: 'bot',
        text: "System reboot completed. 🔄\n\nI'm ready to answer any questions about Karthik's technical inventory, SDE experience, projects, or credentials. What can I retrieve for you?",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
    ]);
    setShowPills(true);
  };

  return (
    <>
      {/* Floating Toggle Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-16 h-16 rounded-full bg-[#121212] border border-white/10 hover:border-secondary/50 flex items-center justify-center text-white shadow-2xl transition-all relative group overflow-hidden ${
            isOpen ? 'bg-secondary' : 'bg-primary'
          }`}
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Toggle AI Assistant"
        >
          {/* Neon Pulse Effect when closed */}
          {!isOpen && (
            <span className="absolute inset-0 rounded-full border border-secondary animate-ping opacity-20 pointer-events-none" />
          )}

          {/* Rotating ambient gradient */}
          <div className="absolute inset-0 bg-gradient-to-tr from-secondary/20 to-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-full" />

          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <X size={24} />
              </motion.div>
            ) : (
              <motion.div
                key="chat"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex items-center justify-center text-secondary relative"
              >
                <Sparkles size={24} className="text-secondary animate-pulse" />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      </div>

      {/* Chat Window Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="fixed bottom-24 right-6 w-[92vw] sm:w-[400px] h-[550px] bg-[#0A0A0A]/95 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] shadow-2xl flex flex-col z-50 overflow-hidden"
          >
            {/* Ambient upper background gradient */}
            <div className="absolute top-0 inset-x-0 h-40 bg-gradient-to-b from-secondary/10 to-transparent pointer-events-none rounded-t-[2.5rem]" />

            {/* Header */}
            <div className="p-6 border-b border-white/5 flex items-center justify-between relative z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-secondary/10 border border-secondary/20 rounded-xl flex items-center justify-center text-secondary relative">
                  <Bot size={20} />
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-[#0A0A0A] rounded-full" />
                </div>
                <div>
                  <h4 className="text-sm font-black text-white uppercase tracking-wider leading-none mb-1">Gouda AI</h4>
                  <div className="flex items-center gap-1.5 text-[10px] text-gray-400 font-bold uppercase tracking-widest leading-none">
                    <span>SDE Representative</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={resetChat}
                  className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 flex items-center justify-center text-gray-400 hover:text-white transition-all"
                  title="Reboot Session"
                >
                  <RotateCcw size={14} />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 flex items-center justify-center text-gray-400 hover:text-white transition-all"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* Messages Screen */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 scrollbar-hide relative z-10">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-3 max-w-[85%] ${
                    msg.sender === 'user' ? 'ml-auto flex-row-reverse' : 'mr-auto'
                  }`}
                >
                  {/* Icon */}
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${
                      msg.sender === 'user'
                        ? 'bg-primary/20 border-primary/20 text-primary'
                        : 'bg-[#121212] border-white/5 text-secondary'
                    }`}
                  >
                    {msg.sender === 'user' ? <User size={14} /> : <Bot size={14} />}
                  </div>

                  {/* Body Text */}
                  <div className="space-y-1">
                    <div
                      className={`px-4 py-3 rounded-2xl text-sm font-medium leading-relaxed whitespace-pre-line ${
                        msg.sender === 'user'
                          ? 'bg-secondary text-white rounded-tr-none'
                          : 'bg-white/5 border border-white/5 text-gray-300 rounded-tl-none'
                      }`}
                    >
                      {renderMessageText(msg.text)}
                    </div>
                    <span
                      className={`text-[9px] font-bold text-gray-500 uppercase tracking-widest block ${
                        msg.sender === 'user' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {msg.timestamp}
                    </span>
                  </div>
                </div>
              ))}

              {/* Typing State */}
              {isTyping && (
                <div className="flex gap-3 mr-auto max-w-[85%]">
                  <div className="w-8 h-8 rounded-lg bg-[#121212] border border-white/5 text-secondary flex items-center justify-center shrink-0">
                    <Bot size={14} />
                  </div>
                  <div className="bg-white/5 border border-white/5 px-4 py-3 rounded-2xl rounded-tl-none flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              )}

              <div ref={chatEndRef} />
            </div>

            {/* Quick Suggestion Pills */}
            {showPills && (
              <div className="px-6 py-2 border-t border-white/5 relative z-10 bg-[#0A0A0A]">
                <div className="flex flex-wrap gap-2 overflow-x-auto py-1 scrollbar-hide">
                  {AIAssistantKnowledge.map((item) => (
                    <button
                      key={item.title}
                      onClick={() => handleQuery(item.keywords[0])}
                      className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-xl text-[10px] font-black text-gray-400 uppercase tracking-widest hover:bg-secondary/10 hover:border-secondary/20 hover:text-white transition-all whitespace-nowrap"
                    >
                      {item.title.split(' ')[0]} {item.keywords[0]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Bottom Actions Form */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleQuery(inputVal);
              }}
              className="p-6 border-t border-white/5 flex gap-3 relative z-10 bg-[#0A0A0A]/95"
            >
              <input
                required
                type="text"
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                placeholder="Ask about skills, projects, internships..."
                className="flex-1 bg-white/5 border border-white/10 focus:border-secondary/30 rounded-2xl px-5 py-4 text-xs text-white placeholder-gray-500 font-medium outline-none transition-all"
              />
              <button
                type="submit"
                className="w-12 h-12 rounded-2xl bg-secondary text-white hover:bg-secondary/90 flex items-center justify-center shadow-lg transition-all"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIAssistant;
